import { parseISO } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

import type { AmPm, TimeValue } from "./eventForm.model";

export type RecurrenceType =
  | "DAILY"
  | "WEEKLY"
  | "MONTHLY"
  | "ANNUALLY"
  | "WEEKDAY";

type TranslateFn = (
  key: string,
  params?: Record<string, string | number>,
) => string;

const DAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

const DAY_KEY_PARTS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

const ORDINAL_KEY_PARTS = [
  "first",
  "second",
  "third",
  "fourth",
  "last",
] as const;

const MONTH_ABBRS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const BYDAY_TO_RECURRENCE_KEY: Record<string, string> = {
  MO: "event_recurrence_every_monday_short",
  TU: "event_recurrence_every_tuesday_short",
  WE: "event_recurrence_every_wednesday_short",
  TH: "event_recurrence_every_thursday_short",
  FR: "event_recurrence_every_friday_short",
  SA: "event_recurrence_every_saturday_short",
  SU: "event_recurrence_every_sunday_short",
};

const DAY_CODE_TO_KEY_PART: Record<string, string> = {
  MO: "monday",
  TU: "tuesday",
  WE: "wednesday",
  TH: "thursday",
  FR: "friday",
  SA: "saturday",
  SU: "sunday",
};

const ORDINAL_NUMBER_TO_KEY_PART: Record<number, string> = {
  1: "first",
  2: "second",
  3: "third",
  4: "fourth",
  [-1]: "last",
};

const MON_THROUGH_FRI = new Set(["MO", "TU", "WE", "TH", "FR"]);
const WEEKDAY_CODES = new Set(["MO", "TU", "WE", "TH", "FR", "SA", "SU"]);

type Byday = { ordinal: number | null; day: string };

type ParsedRrule = {
  freq: string | null;
  interval: number;
  hasEnd: boolean;
  byday: Byday[];
  bymonth: number[];
  bymonthday: number[];
  hasUnsupportedBy: boolean;
};

// Mirroring iOS `RecurrenceRule.parseRrule` — a malformed BYDAY token (or any
// unsupported BY* rule) marks the whole RRULE unsupported, degrading to the
// generic "Recurring event" label via `recognizedPattern`.
function parseRrule(rrule: string): ParsedRrule {
  const parsed: ParsedRrule = {
    freq: null,
    interval: 1,
    hasEnd: false,
    byday: [],
    bymonth: [],
    bymonthday: [],
    hasUnsupportedBy: false,
  };
  const trimmed = rrule.trim();
  const content = trimmed.toUpperCase().startsWith("RRULE:")
    ? trimmed.slice(6)
    : trimmed;
  for (const part of content.split(";")) {
    const [rawKey, rawValue] = part.split("=", 2);
    if (!rawKey || rawValue === undefined) continue;
    const key = rawKey.trim().toUpperCase();
    const value = rawValue.trim();
    switch (key) {
      case "FREQ":
        parsed.freq = value.toUpperCase();
        break;
      case "INTERVAL": {
        const n = Number(value);
        if (Number.isFinite(n)) parsed.interval = n;
        break;
      }
      case "COUNT":
      case "UNTIL":
        parsed.hasEnd = true;
        break;
      case "BYDAY":
        for (const entry of value.split(",")) {
          const m = entry.trim().match(/^(-?\d+)?([A-Z]{2})$/i);
          if (!m) {
            parsed.hasUnsupportedBy = true;
            continue;
          }
          parsed.byday.push({
            ordinal: m[1] ? Number(m[1]) : null,
            day: m[2].toUpperCase(),
          });
        }
        break;
      case "BYMONTH":
        parsed.bymonth = value
          .split(",")
          .map((v) => Number(v.trim()))
          .filter((n) => Number.isFinite(n));
        break;
      case "BYMONTHDAY":
        parsed.bymonthday = value
          .split(",")
          .map((v) => Number(v.trim()))
          .filter((n) => Number.isFinite(n));
        break;
      case "BYYEARDAY":
      case "BYWEEKNO":
      case "BYHOUR":
      case "BYMINUTE":
      case "BYSECOND":
      case "BYSETPOS":
        parsed.hasUnsupportedBy = true;
        break;
      default:
        break;
    }
  }
  return parsed;
}

type RecognizedPattern =
  | { kind: "annually" }
  | { kind: "daily" }
  | { kind: "monthly" }
  | {
      kind: "monthlyOnOrdinalWeekday";
      ordinal: 1 | 2 | 3 | 4 | -1;
      day: string;
    }
  | { kind: "weekdays" }
  | { kind: "every"; day: string };

/**
 * Classify an RRULE into the patterns we have localized strings for. Mirrors
 * iOS `RecurrenceRule.recognizedPattern` — rules that need INTERVAL≠1,
 * COUNT/UNTIL, BYSETPOS/BYHOUR/etc., or shapes outside the small per-frequency
 * whitelist return `null`, which renders the generic "Recurring event" copy.
 */
function recognizedPattern(parsed: ParsedRrule): RecognizedPattern | null {
  if (parsed.interval !== 1 || parsed.hasEnd || parsed.hasUnsupportedBy) {
    return null;
  }
  switch (parsed.freq) {
    case "DAILY":
      if (
        parsed.byday.length > 0 ||
        parsed.bymonth.length > 0 ||
        parsed.bymonthday.length > 0
      ) {
        return null;
      }
      return { kind: "daily" };
    case "WEEKLY": {
      if (parsed.bymonth.length > 0 || parsed.bymonthday.length > 0)
        return null;
      const days = new Set<string>();
      for (const entry of parsed.byday) {
        if (entry.ordinal !== null) return null;
        if (!WEEKDAY_CODES.has(entry.day)) return null;
        if (days.has(entry.day)) return null;
        days.add(entry.day);
      }
      if (days.size === 1) {
        return { kind: "every", day: [...days][0] };
      }
      if (days.size === 5 && [...MON_THROUGH_FRI].every((d) => days.has(d))) {
        return { kind: "weekdays" };
      }
      return null;
    }
    case "MONTHLY": {
      if (parsed.bymonth.length > 0) return null;
      const bydayCount = parsed.byday.length;
      const bymonthdayCount = parsed.bymonthday.length;
      if (bydayCount === 0 && bymonthdayCount <= 1) {
        return { kind: "monthly" };
      }
      if (bydayCount === 1 && bymonthdayCount === 0) {
        const entry = parsed.byday[0];
        if (entry.ordinal === null || !WEEKDAY_CODES.has(entry.day)) {
          return { kind: "monthly" };
        }
        if (
          entry.ordinal === 1 ||
          entry.ordinal === 2 ||
          entry.ordinal === 3 ||
          entry.ordinal === 4 ||
          entry.ordinal === -1
        ) {
          return {
            kind: "monthlyOnOrdinalWeekday",
            ordinal: entry.ordinal,
            day: entry.day,
          };
        }
        return { kind: "monthly" };
      }
      return null;
    }
    case "YEARLY": {
      if (parsed.byday.length > 0) return null;
      const monthsCount = parsed.bymonth.length;
      const daysCount = parsed.bymonthday.length;
      if (
        (monthsCount === 0 && daysCount === 0) ||
        (monthsCount === 1 && daysCount === 1)
      ) {
        return { kind: "annually" };
      }
      return null;
    }
    default:
      return null;
  }
}

function getNthWeekdayOfMonth(date: Date): number {
  return Math.ceil(date.getDate() / 7);
}

export function isWeekday(date: Date): boolean {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

export function buildRrule(type: RecurrenceType, date: Date): string {
  const dayCode = DAY_CODES[date.getDay()];
  switch (type) {
    case "DAILY":
      return "FREQ=DAILY";
    case "WEEKLY":
      return `FREQ=WEEKLY;BYDAY=${dayCode}`;
    case "MONTHLY": {
      const nth = getNthWeekdayOfMonth(date);
      const n = nth >= 5 ? -1 : nth;
      return `FREQ=MONTHLY;BYDAY=${n}${dayCode}`;
    }
    case "ANNUALLY":
      return `FREQ=YEARLY;BYMONTH=${date.getMonth() + 1};BYMONTHDAY=${date.getDate()}`;
    case "WEEKDAY":
      return "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR";
  }
}

export function parseRruleType(
  rrule: string | null | undefined,
): RecurrenceType | null {
  if (!rrule) return null;
  if (rrule.includes("BYDAY=MO,TU,WE,TH,FR")) return "WEEKDAY";
  if (rrule.includes("FREQ=DAILY")) return "DAILY";
  if (rrule.includes("FREQ=WEEKLY")) return "WEEKLY";
  if (rrule.includes("FREQ=MONTHLY")) return "MONTHLY";
  if (rrule.includes("FREQ=YEARLY")) return "ANNUALLY";
  return null;
}

export function getRruleLabel(
  rrule: string | null | undefined,
  t: TranslateFn,
): string | null {
  if (!rrule) return null;
  if (rrule.includes("BYDAY=MO,TU,WE,TH,FR"))
    return t("event_recurrence_weekdays_short");
  if (rrule.includes("FREQ=DAILY")) return t("event_recurrence_daily_short");
  if (rrule.includes("FREQ=WEEKLY")) {
    const match = rrule.match(/BYDAY=([A-Z]{2})/);
    const key = match ? (BYDAY_TO_RECURRENCE_KEY[match[1]] ?? null) : null;
    return key ? t(key) : t("event_recurrence_unrecognized_short");
  }
  if (rrule.includes("FREQ=MONTHLY"))
    return t("event_recurrence_monthly_short");
  if (rrule.includes("FREQ=YEARLY"))
    return t("event_recurrence_annually_short");
  return t("event_recurrence_unrecognized_short");
}

export function getRruleLongLabel(
  rrule: string | null | undefined,
  t: TranslateFn,
): string | null {
  if (!rrule) return null;
  const pattern = recognizedPattern(parseRrule(rrule));
  if (!pattern) return t("event_recurrence_unrecognized_long");
  switch (pattern.kind) {
    case "daily":
      return t("event_recurrence_daily_long");
    case "weekdays":
      return t("event_recurrence_weekdays_long");
    case "every": {
      const dayPart = DAY_CODE_TO_KEY_PART[pattern.day];
      return dayPart
        ? t(`event_recurrence_every_${dayPart}_long`)
        : t("event_recurrence_unrecognized_long");
    }
    case "monthly":
      return t("event_recurrence_monthly_long");
    case "monthlyOnOrdinalWeekday": {
      const ordinalPart = ORDINAL_NUMBER_TO_KEY_PART[pattern.ordinal];
      const dayPart = DAY_CODE_TO_KEY_PART[pattern.day];
      return ordinalPart && dayPart
        ? t(`event_recurrence_monthly_${ordinalPart}_${dayPart}_long`)
        : t("event_recurrence_monthly_long");
    }
    case "annually":
      return t("event_recurrence_annually_long");
  }
}

export function getRecurrenceOptions(
  date: Date,
  t: TranslateFn,
): { value: RecurrenceType; label: string }[] {
  const dayPart = DAY_KEY_PARTS[date.getDay()];
  const nth = getNthWeekdayOfMonth(date);
  const ordinalPart =
    nth >= 5 ? ORDINAL_KEY_PARTS[4] : ORDINAL_KEY_PARTS[nth - 1];
  const monthAbbr = MONTH_ABBRS[date.getMonth()];
  const dateStr = `${monthAbbr} ${date.getDate()}`;
  const options: { value: RecurrenceType; label: string }[] = [
    { value: "DAILY", label: t("event_recurrence_option_daily") },
    {
      value: "WEEKLY",
      label: t(`event_recurrence_option_weekly_on_${dayPart}`),
    },
    {
      value: "MONTHLY",
      label: t(`event_recurrence_option_monthly_${ordinalPart}_${dayPart}`),
    },
    {
      value: "ANNUALLY",
      label: t("event_recurrence_option_annually_on", { "0": dateStr }),
    },
  ];
  if (isWeekday(date)) {
    options.push({
      value: "WEEKDAY",
      label: t("event_recurrence_option_weekday"),
    });
  }
  return options;
}

/**
 * Canonical UTC ISO 8601 string (`…Z`) for form state and PATCH `duration_start_utc`.
 * Uses `formatInTimeZone(…, 'UTC', …)` so the result is not localized to the runtime timezone.
 * Pairs with `z.iso.datetime()` in the event form schema.
 */
export function toUtcIsoString(isoInstant: string): string {
  return formatInTimeZone(
    parseISO(isoInstant),
    "UTC",
    "yyyy-MM-dd'T'HH:mm:ss.SSSX",
  );
}

/**
 * When the user picks a **wall-clock** time in the event’s IANA zone (e.g. from a picker),
 * convert it to the UTC ISO string the API expects.
 *
 * Display path: use {@link formatEventDate} / {@link formatEventTimeRange} with the stored UTC
 * string + `event.timezone` so users see local event time, not browser TZ.
 */
export function zonedLocalDateToUtcIsoString(
  localDateInEventZone: Date,
  eventIanaTimezone: string,
): string {
  const utc = fromZonedTime(localDateInEventZone, eventIanaTimezone);
  return toUtcIsoString(utc.toISOString());
}

export function formatEventDate(isoString: string, timezone: string): string {
  return formatInTimeZone(new Date(isoString), timezone, "MMMM do, yyyy");
}

/**
 * 11:59 PM as minutes since midnight: the latest end an event can have, since
 * the start and end share one date.
 */
export const LAST_MINUTE_OF_DAY = 23 * 60 + 59;

/**
 * Default end time when the user first adds an end to an event: 1 hour after
 * the start time, capped at 11:59 PM instead of crossing midnight.
 */
export function getDefaultEndTime(fromTime: TimeValue): TimeValue {
  return minutesToTimeValue(
    Math.min(timeToMinutes(fromTime) + 60, LAST_MINUTE_OF_DAY),
  );
}

/**
 * The start that keeps an event's length when its end moves to `newEnd`,
 * clamped to 12:00 AM instead of crossing into the day before. An event with
 * no length to keep is given an hour, as {@link getDefaultEndTime} does.
 */
export function getStartKeepingDuration(
  start: TimeValue,
  end: TimeValue,
  newEnd: TimeValue,
): TimeValue {
  const length = timeToMinutes(end) - timeToMinutes(start);
  return minutesToTimeValue(
    Math.max(timeToMinutes(newEnd) - (length > 0 ? length : 60), 0),
  );
}

function timeValueToHour24(time: TimeValue): number {
  const { hour, amPm } = time;
  return amPm === "AM"
    ? hour === 12
      ? 0
      : hour
    : hour === 12
      ? 12
      : hour + 12;
}

/** Minutes since midnight for a 12h {@link TimeValue} (0–1439). */
export function timeToMinutes(time: TimeValue): number {
  return timeValueToHour24(time) * 60 + time.minute;
}

/** Minutes since midnight (0–1439) as a 12h {@link TimeValue}. */
export function minutesToTimeValue(minutes: number): TimeValue {
  const h24 = Math.floor(minutes / 60);
  return {
    hour: h24 % 12 === 0 ? 12 : h24 % 12,
    minute: minutes % 60,
    amPm: h24 < 12 ? "AM" : "PM",
  };
}

/**
 * A time typed by hand, read leniently: "7:10 PM", "7:10pm", "7:10p", "710pm",
 * "7pm", "7 p", "19:10", "1910", "0:30". With no AM/PM, an hour of 1–11 keeps
 * `currentAmPm`, 12 is noon, and 0 or 13–23 is read as 24-hour time. `null`
 * when the text is not a time.
 */
export function parseTimeInput(
  input: string,
  currentAmPm: AmPm,
): TimeValue | null {
  const match = /^(\d{1,2}):?(\d{2})?(?:([ap])m?)?$/.exec(
    input.toLowerCase().replace(/[\s.]/g, ""),
  );
  if (!match) return null;
  const [, hourText, minuteText, meridiem] = match;
  const hour = Number(hourText);
  const minute = minuteText ? Number(minuteText) : 0;
  if (minute > 59) return null;

  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    return { hour, minute, amPm: meridiem === "a" ? "AM" : "PM" };
  }
  if (hour === 12) return { hour, minute, amPm: "PM" };
  if (hour >= 1 && hour <= 11) return { hour, minute, amPm: currentAmPm };
  if (hour > 23) return null;
  return minutesToTimeValue(hour * 60 + minute);
}

/**
 * Calendar day + wall-clock time as a `Date` in the browser’s local timezone constructor
 * (used with {@link zonedLocalDateToUtcIsoString} for the event IANA zone).
 */
export function buildLocalDate(calendarDay: Date, time: TimeValue): Date {
  const h24 = timeValueToHour24(time);
  return new Date(
    calendarDay.getFullYear(),
    calendarDay.getMonth(),
    calendarDay.getDate(),
    h24,
    time.minute,
    0,
    0,
  );
}

/** UTC ISO instant → date + {@link TimeValue} in `timezone`, to the minute. */
export function parseLocalTime(
  utcIso: string,
  timezone: string,
): { date: Date; time: TimeValue } {
  const localStr = formatInTimeZone(
    parseISO(utcIso),
    timezone,
    "yyyy-MM-dd HH:mm",
  );
  const [datePart, timePart] = localStr.split(" ");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h24, m] = timePart.split(":").map(Number);

  return {
    date: new Date(y, mo - 1, d),
    time: minutesToTimeValue(h24 * 60 + m),
  };
}

export function formatEventTimeRange(
  startIso: string,
  durationSeconds: number,
  timezone: string,
): string {
  const start = new Date(startIso);
  const end = new Date(start.getTime() + durationSeconds * 1000);
  const fmt = (d: Date) => formatInTimeZone(d, timezone, "h:mm a");
  return `${fmt(start)} – ${fmt(end)}`;
}

/**
 * Short localized date for UI labels (weekday + month + day).
 * `locales` is `undefined` so the engine uses the default locale (browser locale in the client;
 * host/OS default under Node, e.g. SSR).
 */
export function formatShortDateLabel(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** `TimeValue` as `H:MM AM/PM` for chips, pickers, and similar. */
export function formatTimeValueLabel(
  hour: number,
  minute: number,
  amPm: AmPm,
): string {
  return `${hour}:${String(minute).padStart(2, "0")} ${amPm}`;
}
